import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Close,
  ExitToApp,
  RoomService,
  LocalLaundryService,
  Restaurant,
  Wifi,
  Key,
  Receipt,
  CheckCircle,
  Warning,
  Add,
  Delete,
} from '@mui/icons-material';
import { format } from 'date-fns';

export default function CheckOutDialog({ open, onClose, booking, onCheckOut }) {
  const [formData, setFormData] = useState({
    roomInspected: false,
    keysReturned: false,
    minibarChecked: false,
    damagesChecked: false,
    actualCheckOutTime: format(new Date(), 'HH:mm'),
    paymentCleared: false,
    feedbackCollected: false,
    guestRating: '',
    checkoutNotes: '',
  });

  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [newCharge, setNewCharge] = useState({ description: '', amount: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleAddCharge = () => {
    if (newCharge.description && newCharge.amount) {
      setAdditionalCharges([
        ...additionalCharges,
        { ...newCharge, amount: parseFloat(newCharge.amount) }
      ]);
      setNewCharge({ description: '', amount: '' });
    }
  };

  const handleRemoveCharge = (index) => {
    setAdditionalCharges(additionalCharges.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const bookingAmount = parseFloat(booking?.amount?.replace('$', '') || 0);
    const additionalTotal = additionalCharges.reduce((sum, charge) => sum + charge.amount, 0);
    return bookingAmount + additionalTotal;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.roomInspected) {
      newErrors.roomInspected = 'Room inspection is required';
    }
    if (!formData.keysReturned) {
      newErrors.keysReturned = 'Please confirm all keys are returned';
    }
    if (!formData.minibarChecked) {
      newErrors.minibarChecked = 'Please verify minibar usage';
    }
    if (!formData.paymentCleared) {
      newErrors.paymentCleared = 'Payment must be cleared before checkout';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckOut = () => {
    if (!validateForm()) return;

    const checkOutData = {
      ...booking,
      status: 'Checked Out',
      actualCheckOutTime: formData.actualCheckOutTime,
      actualCheckOutDate: format(new Date(), 'yyyy-MM-dd'),
      additionalCharges,
      totalAmount: `$${calculateTotal().toFixed(2)}`,
      guestRating: formData.guestRating,
      checkOutNotes: formData.checkoutNotes,
      feedbackCollected: formData.feedbackCollected,
    };

    onCheckOut(checkOutData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      roomInspected: false,
      keysReturned: false,
      minibarChecked: false,
      damagesChecked: false,
      actualCheckOutTime: format(new Date(), 'HH:mm'),
      paymentCleared: false,
      feedbackCollected: false,
      guestRating: '',
      checkoutNotes: '',
    });
    setAdditionalCharges([]);
    setNewCharge({ description: '', amount: '' });
    setErrors({});
    onClose();
  };

  if (!booking) return null;

  const allChecksPassed = 
    formData.roomInspected && 
    formData.keysReturned && 
    formData.minibarChecked && 
    formData.paymentCleared;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <ExitToApp color="primary" />
            <Box>
              <Typography variant="h6">Check-Out Guest</Typography>
              <Typography variant="caption" color="text.secondary">
                Booking ID: {booking?.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Guest Information Summary */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Guest Name
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking?.guest}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Room
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking?.room}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Check-In
                    </Typography>
                    <Typography variant="body2">
                      {booking?.checkIn}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Check-Out
                    </Typography>
                    <Typography variant="body2">
                      {booking?.checkOut}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Nights Stayed
                    </Typography>
                    <Typography variant="body2">
                      {booking?.nights}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Check-Out Requirements */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Check-Out Requirements
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {(errors.roomInspected || errors.keysReturned || errors.minibarChecked || errors.paymentCleared) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please complete all required check-out steps before proceeding.
              </Alert>
            )}

            <List>
              <ListItem>
                <ListItemIcon>
                  <RoomService color={formData.roomInspected ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Room Inspection"
                  secondary="Inspect room for damages and cleanliness"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.roomInspected}
                      onChange={(e) => handleChange('roomInspected', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Inspected"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Key color={formData.keysReturned ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Return Room Keys"
                  secondary="Collect all room key cards from guest"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.keysReturned}
                      onChange={(e) => handleChange('keysReturned', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Returned"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Restaurant color={formData.minibarChecked ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Minibar Check"
                  secondary="Verify minibar usage and add charges if applicable"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.minibarChecked}
                      onChange={(e) => handleChange('minibarChecked', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Checked"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Warning color={formData.damagesChecked ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Check for Damages"
                  secondary="Inspect for any damages to room or property (optional)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.damagesChecked}
                      onChange={(e) => handleChange('damagesChecked', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Checked"
                />
              </ListItem>
            </List>
          </Grid>

          {/* Additional Charges */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Additional Charges
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={newCharge.description}
                  onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
                  placeholder="e.g., Minibar, Room Service"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Amount"
                  type="number"
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleAddCharge}
                  startIcon={<Add />}
                  disabled={!newCharge.description || !newCharge.amount}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            {additionalCharges.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {additionalCharges.map((charge, index) => (
                      <TableRow key={index}>
                        <TableCell>{charge.description}</TableCell>
                        <TableCell align="right">${charge.amount.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleRemoveCharge(index)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Card sx={{ bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Original Booking Amount</Typography>
                  <Typography>{booking?.amount}</Typography>
                </Box>
                {additionalCharges.length > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Additional Charges</Typography>
                    <Typography>
                      ${additionalCharges.reduce((sum, c) => sum + c.amount, 0).toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total Amount</Typography>
                  <Typography variant="h6" color="primary">
                    ${calculateTotal().toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Clearance */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Payment & Feedback
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Check-Out Time"
              type="time"
              value={formData.actualCheckOutTime}
              onChange={(e) => handleChange('actualCheckOutTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Guest Rating"
              value={formData.guestRating}
              onChange={(e) => handleChange('guestRating', e.target.value)}
            >
              <MenuItem value="">Not Collected</MenuItem>
              <MenuItem value="5">⭐⭐⭐⭐⭐ Excellent</MenuItem>
              <MenuItem value="4">⭐⭐⭐⭐ Good</MenuItem>
              <MenuItem value="3">⭐⭐⭐ Average</MenuItem>
              <MenuItem value="2">⭐⭐ Poor</MenuItem>
              <MenuItem value="1">⭐ Very Poor</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.paymentCleared}
                  onChange={(e) => handleChange('paymentCleared', e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    All Payments Cleared
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Confirm that all outstanding payments have been settled
                  </Typography>
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.feedbackCollected}
                  onChange={(e) => handleChange('feedbackCollected', e.target.checked)}
                />
              }
              label="Feedback collected from guest (optional)"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Check-Out Notes"
              multiline
              rows={3}
              value={formData.checkoutNotes}
              onChange={(e) => handleChange('checkoutNotes', e.target.value)}
              placeholder="Any issues, feedback, or notes..."
            />
          </Grid>

          {allChecksPassed && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckCircle />}>
                All check-out requirements completed! Ready to check out the guest.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCheckOut}
          disabled={!allChecksPassed}
          startIcon={<ExitToApp />}
        >
          Complete Check-Out
        </Button>
      </DialogActions>
    </Dialog>
  );
}
